import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PencilSimple, FloppyDisk } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";

const relativeTime = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export function DraftPreviewModal({ draftPreview, onClose, onResume, onSave, savingDraft }) {
  return (
    <Dialog open={!!draftPreview} onOpenChange={() => onClose()}>
      <DialogContent className="bg-[var(--bg-raised)] border-[var(--border-strong)] text-[var(--text-primary)] max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="draft-preview-modal">
        <DialogHeader>
          <DialogTitle className="text-[var(--text-primary)]">
            Draft: {draftPreview?.generation_context?.topic || draftPreview?.content_type || "Untitled"}
          </DialogTitle>
        </DialogHeader>
        {draftPreview && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                {draftPreview.content_type}
              </span>
              {draftPreview.generation_context?.platform && (
                <span className="px-2 py-0.5 rounded bg-[var(--bg-hover)] text-[var(--text-secondary)]">
                  {draftPreview.generation_context.platform}
                </span>
              )}
              <span className="px-2 py-0.5 rounded bg-[var(--bg-hover)] text-[var(--text-muted)]">
                Last saved {relativeTime(draftPreview.updated_at)}
              </span>
            </div>
            <div className="space-y-3">
              {Object.entries(draftPreview.sections || {}).map(([name, content]) => (
                <div key={name} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg overflow-hidden">
                  <div className="px-4 py-2 border-b border-[var(--border)]">
                    <h4 className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.15em] text-[#FF4400]">
                      {name}
                    </h4>
                  </div>
                  <div className="px-4 py-3 text-sm text-[var(--text-secondary)] leading-relaxed max-h-[200px] overflow-y-auto prose prose-sm max-w-none prose-headings:text-[var(--text-primary)] prose-strong:text-[var(--text-primary)] prose-p:text-[var(--text-secondary)]">
                    <ReactMarkdown>{content}</ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-[var(--border-strong)] text-[var(--text-secondary)]" data-testid="draft-preview-close">
            Close
          </Button>
          <Button
            variant="outline"
            onClick={() => onResume(draftPreview)}
            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
            data-testid="draft-preview-resume"
          >
            <PencilSimple size={16} className="mr-1.5" />
            Resume Editing
          </Button>
          <Button
            onClick={() => onSave(draftPreview)}
            disabled={savingDraft}
            className="bg-[#FF4400] hover:bg-[#FF6633] text-white"
            data-testid="draft-preview-save"
          >
            <FloppyDisk size={16} className="mr-1.5" />
            {savingDraft ? "Saving..." : "Save to History"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
